import { useCallback, useEffect, useRef, useState } from 'react';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';

// Watches Leaflet movement and exposes the short settling/refining states used
// by the radar loading message.
export default function useDopplerMapViewState({
  map,
  onMovementStart,
  onViewSettled,
  onViewStable,
}) {
  const [isMapReady, setIsMapReady] = useState(false);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [isViewStabilizing, setIsViewStabilizing] = useState(false);
  const settleTimeoutRef = useRef(null);
  const stabilizeTimeoutRef = useRef(null);
  const settleAnimationFrameRef = useRef(null);

  const clearViewTimeouts = useCallback(() => {
    if (settleTimeoutRef.current) {
      window.clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
    if (stabilizeTimeoutRef.current) {
      window.clearTimeout(stabilizeTimeoutRef.current);
      stabilizeTimeoutRef.current = null;
    }
    if (settleAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(settleAnimationFrameRef.current);
      settleAnimationFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!map) return undefined;

    const handleReady = () => setIsMapReady(true);
    const handleMoveStart = () => {
      clearViewTimeouts();
      setIsMapMoving(true);
      setIsViewStabilizing(false);
      onMovementStart?.();
    };
    const handleMoveEnd = () => {
      clearViewTimeouts();
      settleTimeoutRef.current = window.setTimeout(() => {
        setIsMapMoving(false);
        setIsViewStabilizing(true);
        settleAnimationFrameRef.current = window.requestAnimationFrame(() => {
          onViewSettled?.();
          settleAnimationFrameRef.current = null;
        });
        stabilizeTimeoutRef.current = window.setTimeout(() => {
          setIsViewStabilizing(false);
          onViewStable?.();
          stabilizeTimeoutRef.current = null;
        }, DOPPLER_CONFIG.viewportStabilizeDelayMs);
        settleTimeoutRef.current = null;
      }, DOPPLER_CONFIG.viewportSettleDelayMs);
    };

    if (map.getZoom() !== undefined) {
      setIsMapReady(true);
    }

    map.on('load', handleReady);
    map.on('movestart', handleMoveStart);
    map.on('zoomstart', handleMoveStart);
    map.on('moveend', handleMoveEnd);

    return () => {
      map.off('load', handleReady);
      map.off('movestart', handleMoveStart);
      map.off('zoomstart', handleMoveStart);
      map.off('moveend', handleMoveEnd);
      clearViewTimeouts();
    };
  }, [clearViewTimeouts, map, onMovementStart, onViewSettled, onViewStable]);

  return {
    isMapReady,
    isMapMoving,
    isViewStabilizing,
    clearViewTimeouts,
  };
}
